import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Message } from '@arco-design/web-vue';
import { useUserStore } from '@/store';
import { getToken } from '@/utils/auth';
import router from '@/router';

export interface HttpResponse<T = unknown> {
  status: number;
  msg: string;
  code: number;
  data: T;
}

if (import.meta.env.VITE_API_BASE_URL) {
  axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;
}

axios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // 让每个请求都携带token
    const token = getToken();
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.token = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// 添加响应拦截器
axios.interceptors.response.use(
  (response: AxiosResponse<HttpResponse>) => {
    const res = response.data;

    // 用户登录后，根据userRole设置role并保存用户信息到store
    if (response.config.url === '/user/login' && res.code === 1 && res.data) {
      const userRoleToRole: Record<number, string> = {
        2: 'Consultant',
        3: 'TeamLeader',
        4: 'AreaAdmin',
      };

      const loginData = res.data as any;

      // 处理用户信息
      if (loginData.userinfo) {
        if (
          loginData.userinfo.user_role &&
          userRoleToRole[loginData.userinfo.user_role]
        ) {
          loginData.userinfo.role =
            userRoleToRole[loginData.userinfo.user_role];
        }

        // 将用户信息保存到store中，避免后续再次请求用户信息
        const userStore = useUserStore();
        userStore.setInfo(loginData.userinfo);
      }
    }

    // 如果自定义状态码不是1，则判定为错误
    if (res.code !== 1) {
      Message.error({
        content: res.msg || 'Error',
        duration: 5 * 1000,
      });
      // 401:非法Token或Token过期; 403:Token过期;
      if ([401, 403].includes(res.code)) {
        const userStore = useUserStore();

        // 清除用户状态并跳转到登录页
        userStore.forceLogout();

        // 跳转到登录页并清除所有历史记录
        router.replace({
          name: 'login',
          query: {
            redirect: router.currentRoute.value.name as string,
            reason: 'token_expired',
          },
        });
      }
      return Promise.reject(new Error(res.msg || 'Error'));
    }
    return res;
  },
  (error) => {
    // 处理网络错误或服务器返回的错误
    let errorMessage = '请求错误';

    if (error.response) {
      // 服务器返回了错误状态码
      const { status, data } = error.response;

      if (status === 401) {
        // 401未授权，跳转到登录页
        const userStore = useUserStore();
        userStore.forceLogout();
        router.replace({
          name: 'login',
          query: {
            redirect: router.currentRoute.value.name as string,
            reason: 'unauthorized',
          },
        });
        return Promise.reject(error);
      }

      errorMessage = data?.msg || `请求失败 (${status})`;
    } else if (error.request) {
      // 请求已发出但没有收到响应
      errorMessage = '网络连接失败';
    } else {
      // 请求配置出错
      errorMessage = error.message || '请求配置错误';
    }

    Message.error({
      content: errorMessage,
      duration: 5 * 1000,
    });
    return Promise.reject(error);
  }
);
